import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Target } from "lucide-react";

function isoWeekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

export default function GoalsPanel({ currentDate }) {
  const year = currentDate.getFullYear();
  const monthKey = `${year}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
  const weekKey = isoWeekKey(currentDate);

  const periods = {
    year: String(year),
    month: monthKey,
    week: weekKey,
  };

  const [scope, setScope] = useState("year");
  const [goals, setGoals] = useState([]);
  const [title, setTitle] = useState("");

  const load = async () => {
    const { data } = await api.get("/goals", {
      params: { scope, period: periods[scope] },
    });
    setGoals(data);
  };

  useEffect(() => {
    load();
     
  }, [scope, periods[scope]]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await api.post("/goals", {
      title: title.trim(),
      scope,
      period: periods[scope],
    });
    setTitle("");
    load();
  };

  const toggle = async (g) => {
    await api.patch(`/goals/${g.id}`, { completed: !g.completed });
    load();
  };

  const remove = async (g) => {
    await api.delete(`/goals/${g.id}`);
    load();
  };

  const labels = {
    year: `Year ${year}`,
    month: currentDate.toLocaleString("en-US", { month: "long", year: "numeric" }),
    week: weekKey,
  };

  return (
    <div className="space-y-3" data-testid="goals-panel">
      <div className="flex items-center gap-2">
        <Target strokeWidth={1.5} className="size-4 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Goals
        </span>
      </div>

      <Tabs value={scope} onValueChange={setScope}>
        <TabsList className="grid grid-cols-3 w-full h-9 bg-secondary/60">
          <TabsTrigger value="year" data-testid="goals-tab-year" className="text-xs">Year</TabsTrigger>
          <TabsTrigger value="month" data-testid="goals-tab-month" className="text-xs">Month</TabsTrigger>
          <TabsTrigger value="week" data-testid="goals-tab-week" className="text-xs">Week</TabsTrigger>
        </TabsList>
        <div className="text-xs text-muted-foreground mt-2 mb-1">{labels[scope]}</div>

        <TabsContent value={scope} className="mt-0">
          <form onSubmit={add} className="flex gap-2 mb-3">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Add a ${scope} goal...`}
              className="h-9 text-sm"
              data-testid="goal-input"
            />
            <Button type="submit" size="icon" className="h-9 w-9 shrink-0" data-testid="goal-add-button">
              <Plus strokeWidth={1.5} className="size-4" />
            </Button>
          </form>

          <h3
            className="text-sm font-bold mb-1.5"
            data-testid="goals-in-progress-header"
          >
            In progress
          </h3>

          <ul className="space-y-1.5 max-h-64 overflow-y-auto md:max-h-none md:overflow-visible pr-1">
            {goals.length === 0 && (
              <li className="text-xs text-muted-foreground py-4 text-center">No goals yet.</li>
            )}
            {goals.map((g) => (
              <li
                key={g.id}
                data-testid={`goal-item-${g.id}`}
                className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-secondary/60 transition-colors"
              >
                <Checkbox
                  checked={g.completed}
                  onCheckedChange={() => toggle(g)}
                  data-testid={`goal-checkbox-${g.id}`}
                />
                <span
                  className={`text-sm flex-1 transition-all ${
                    g.completed ? "line-through text-muted-foreground" : ""
                  }`}
                >
                  {g.title}
                </span>
                <button
                  onClick={() => remove(g)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all"
                  data-testid={`goal-delete-${g.id}`}
                  aria-label="Delete goal"
                >
                  <Trash2 strokeWidth={1.5} className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </TabsContent>
      </Tabs>
    </div>
  );
}
